import { isAfter, isBefore } from "date-fns";
import i18n from "i18next";

import { GetFacility_facility_hoursOfOperation } from "/apollo/schema/types";
import { parseDateTime } from "/util/date";

export const DAYS_IN_WEEK = 7;

export const isDayClosed = (hours?: GetFacility_facility_hoursOfOperation | null): boolean =>
  !hours?.openingTime || !hours?.closingTime;

export const getHoursForDay = (
  hoursOfOperation: GetFacility_facility_hoursOfOperation[] | null,
  dayIndex: number,
): GetFacility_facility_hoursOfOperation | null =>
  hoursOfOperation?.[dayIndex % DAYS_IN_WEEK] ?? null;

export const findNextOpenDay = (
  hoursOfOperation: GetFacility_facility_hoursOfOperation[] | null,
  fromDayIndex: number,
  startOffset = 0,
): { hours: GetFacility_facility_hoursOfOperation; daysAhead: number } | null => {
  if (!hoursOfOperation) return null;

  for (let offset = startOffset; offset < startOffset + DAYS_IN_WEEK; offset++) {
    const hours = getHoursForDay(hoursOfOperation, fromDayIndex + offset);

    if (hours && !isDayClosed(hours)) {
      return { hours, daysAhead: offset };
    }
  }

  return null;
};

export const isFacilityOpen = (
  hoursOfOperation: GetFacility_facility_hoursOfOperation[] | null,
  today: Date = new Date(),
): boolean => {
  const hoursToday = getHoursForDay(hoursOfOperation, today.getDay());

  if (isDayClosed(hoursToday)) return false;

  const openingDateTime = parseDateTime(hoursToday?.openingTime as string);
  const closingDateTime = parseDateTime(hoursToday?.closingTime as string);

  return isAfter(today, openingDateTime) && isBefore(today, closingDateTime);
};

export const getEstimatedWaitTime = (waitTimeSeconds: number) => {
  const waitTimeMinutes = waitTimeSeconds / 60;
  if (waitTimeMinutes < 5) {
    return i18n.t("facilityUtil-lessThan");
  } else if (waitTimeMinutes > 45) {
    return i18n.t("facilityUtil-moreThan");
  }

  return i18n.t("facilityUtil-timeMinutes", { minutes: waitTimeMinutes });
};
